
import React from 'react';
import { useFeatures } from '../contexts/FeaturesContext';
import AvisEncaissement from '../components/caisse/AvisEncaissement';
import { CheckCircle, User, Phone, GraduationCap, CreditCard, X, Users, Percent, AlertCircle, Loader2, Award } from 'lucide-react';

// Les politiques d'ecole, dites en clair. Les emojis d'origine ne sont pas des
// icones de l'application : la charte n'utilise que Lucide.
const LIBELLES_POLITIQUE = {
  admis_examen: 'Admis aux examens',
  premier_classe: 'Premier de classe',
  premiers_inscrits: 'Parmi les 100 premiers inscrits'
};

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  studentInfo,
  parentInfo,
  registrationType,
  finance,
  classes,
  moisScolaires,
  familleInfo,
  etatsParMois = new Map(),
  isSubmitting = false
}) => {
  // Le hook AVANT tout retour anticipe : React exige que les hooks soient
  // appeles inconditionnellement, a chaque rendu, dans le meme ordre.
  //
  // Sans comptabilite, aucun paiement n a pu etre saisi : le recapitulatif
  // financier n aurait que des compteurs a zero, qu on lirait comme un impaye.
  const { estActive } = useFeatures();
  const comptabiliteOuverte = estActive('module.comptabilite');

  if (!isOpen) return null;

  // FCFA sans decimales, separateur de milliers — convention maison.
  const montantFCFA = (valeur) =>
    `${Math.round(Number(valeur) || 0).toLocaleString('fr-FR').replace(/ | /g, ' ')} FCFA`;

  const totalAnnuelAnnonce =
    (Number(finance?.inscription?.montant) || 0) +
    Object.values(finance?.paiements_mensuels || {}).reduce(
      (somme, m) => somme + (Number(m.montant_attendu) || 0),
      0
    );

  // Le montant reellement du pour un mois, une fois la date d'entree prise en
  // compte : zero s'il est anterieur a l'entree, le prorata s'il est a cheval.
  //
  // Sans cette lecture, le recapitulatif annoncait le tarif plein sur les neuf
  // mois - 36 000 FCFA reclames a un eleve entre le 24 juin, qui n'en doit que
  // 933. C'est le dernier ecran avant l'engagement : il doit dire le vrai.
  const etatDuMois = (moisId) => etatsParMois.get(String(moisId)) || null;

  const montantDuDuMois = (moisId, paiement) => {
    const etat = etatDuMois(moisId);
    if (etat) return etat.montantDu;
    return paiement?.montant_attendu || 0;
  };

  const fcfa = (montant) => `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant) || 0))} FCFA`;

  // Calculs basés sur la nouvelle structure
  const calculateTotalPaid = () => {
    let total = 0;
    if (finance.inscription?.paye) {
      total += finance.inscription.montant || 0;
    }
    if (finance.paiements_mensuels) {
      Object.entries(finance.paiements_mensuels).forEach(([moisId, paiement]) => {
        // Plafonne au du : un montant saisi avant que la date d'entree ne
        // reduise le mois ne doit pas gonfler l'encaissement annonce.
        total += Math.min(paiement.montant_paye || 0, montantDuDuMois(moisId, paiement));
      });
    }
    return total;
  };

  const calculateTotalDue = () => {
    let total = 0;
    total += finance.inscription?.montant || 0;
    if (finance.paiements_mensuels) {
      Object.entries(finance.paiements_mensuels).forEach(([moisId, paiement]) => {
        total += montantDuDuMois(moisId, paiement);
      });
    }
    return total;
  };

  const getClassName = (classeId) => {
    const classe = classes.find(c => c.id === parseInt(classeId));
    return classe ? classe.nom : '';
  };

  const getPaiementsSummary = () => {
    const summary = {
      paye: { count: 0, mois: [], montant: 0, montantNormal: 0, reduction: 0 },
      partiel: { count: 0, mois: [], montant: 0 },
      nonPaye: { count: 0, mois: [], montant: 0, montantNormal: 0, reduction: 0 },
      nonScolarise: { count: 0, mois: [] },
      prorata: { count: 0, lignes: [] }
    };

    if (finance.paiements_mensuels && moisScolaires) {
      Object.entries(finance.paiements_mensuels).forEach(([cleMois, paiement]) => {
        const etat = etatDuMois(cleMois);
        const montantDu = montantDuDuMois(cleMois, paiement);
        const montantPaye = Math.min(paiement.montant_paye || 0, montantDu);
        const montantNormal = paiement.montant_normal || montantDu;
        const reduction = paiement.montant_reduction || 0;
        const moisInfo = moisScolaires.find(m => String(m.id) === String(cleMois));
        const moisNom = moisInfo ? moisInfo.nom : `Mois ${cleMois}`;

        // Un mois anterieur a l'entree n'est pas un impaye : rien n'est du.
        if (etat?.type === 'non_scolarise') {
          summary.nonScolarise.count++;
          summary.nonScolarise.mois.push(moisNom);
          return;
        }

        // Le mois d'entree proratise garde sa place dans le decompte des
        // paiements, mais il est signale a part : son montant reduit est
        // integralement du, ce n'est pas un rabais.
        if (etat?.type === 'prorata') {
          summary.prorata.count++;
          summary.prorata.lignes.push({
            mois: moisNom,
            montantDu,
            montantPlein: etat.montantPlein,
            joursPresents: etat.joursPresents,
            joursDuMois: etat.joursDuMois
          });
        }

        if (montantDu === 0) {
          return;
        } else if (montantPaye >= montantDu) {
          summary.paye.count++;
          summary.paye.mois.push(moisNom);
          summary.paye.montant += montantPaye;
          summary.paye.montantNormal += montantNormal;
          summary.paye.reduction += reduction;
        } else if (montantPaye > 0) {
          summary.partiel.count++;
          summary.partiel.mois.push(moisNom);
          summary.partiel.montant += montantPaye;
        } else {
          summary.nonPaye.count++;
          summary.nonPaye.mois.push(moisNom);
          summary.nonPaye.montant += montantDu;
          summary.nonPaye.montantNormal += montantNormal;
          summary.nonPaye.reduction += reduction;
        }
      });
    }
    return summary;
  };

  const totalPaid = calculateTotalPaid();
  const totalDue = calculateTotalDue();
  const remaining = totalDue - totalPaid;
  const paiementsSummary = getPaiementsSummary();
  const hasReduction = (
  familleInfo?.politique_ecole ||
  familleInfo?.appartient_famille ||
  familleInfo?.reduction_exceptionnelle
);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 10000
      }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl" style={{ zIndex: 10001 }}>
        {/* Header */}
        <div className="bg-[#246990] px-5 py-3 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} className="text-white" />
            <h2 className="text-lg font-semibold text-white">Confirmation d'inscription</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-12 gap-5">
            {/* Colonne gauche : Infos (3 cols) */}
            <div className="col-span-3 space-y-3">
              {/* Élève */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-300">
                  <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center">
                    <User size={14} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Élève</h3>
                </div>
                <div className="space-y-2 text-xs">
                  <div>
                    <p className="text-gray-500 mb-0.5">Nom complet</p>
                    <p className="font-semibold text-gray-900">{studentInfo.nom} {studentInfo.prenom}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Date de naissance</p>
                    <p className="text-gray-700">{new Date(studentInfo.dateNaissance).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Nationalité</p>
                    <p className="text-gray-700">{studentInfo.nationalite}</p>
                  </div>
                </div>
              </div>

              {/* Parent */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-300">
                  <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center">
                    <Phone size={14} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Parent/Tuteur</h3>
                </div>
                <div className="space-y-2 text-xs">
                  <div>
                    <p className="text-gray-500 mb-0.5">Nom</p>
                    <p className="font-semibold text-gray-900">{parentInfo.nom_prenom}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Téléphone</p>
                    <p className="text-gray-700">{parentInfo.telephone}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Email</p>
                    <p className="text-gray-700 break-all">{parentInfo.email}</p>
                  </div>
                </div>
              </div>

              {/* Scolarité */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-300">
                  <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center">
                    <GraduationCap size={14} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Scolarité</h3>
                </div>
                <div className="space-y-2 text-xs">
                  <div>
                    <p className="text-gray-500 mb-0.5">Classe</p>
                    <p className="font-bold text-gray-900 text-base">{getClassName(studentInfo.classe_id)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Type</p>
                    <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                      {registrationType === 'new' ? 'Nouvelle inscription' : 'Réinscription'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Colonne droite : Finance (9 cols) */}
            <div className="col-span-9 space-y-4">
              {/* Réduction */}


           {/* Réduction */}
          {/* Meme bandeau que l'ecran des frais, meme palette : la caisse doit
              reconnaitre du premier coup d'oeil l'information qu'elle vient de
              valider. Les trois cas partagent la structure pour que les montants
              tombent dans la meme colonne. */}
          {hasReduction && (() => {
            const source = familleInfo.politique_ecole
              ? {
                Icone: Award,
                titre: "Politique d'école",
                detail: LIBELLES_POLITIQUE[familleInfo.politique_type] || "Politique d'école"
              }
              : familleInfo.appartient_famille
                ? { Icone: Users, titre: 'Famille nombreuse', detail: familleInfo.famille_nom }
                : { Icone: Percent, titre: 'Réduction exceptionnelle', detail: null };

            const { Icone } = source;
            const reductionMensuelle = Number(familleInfo.reduction_mensuel) || 0;
            const reductionInscription = Number(familleInfo.reduction_inscription) || 0;

            return (
              <div className="rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3 flex items-center gap-3">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#e7f3f8] text-[#246990]">
                  <Icone size={16} />
                </span>

                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm">{source.titre}</h4>
                  {source.detail && (
                    <p className="text-xs text-gray-600 truncate">{source.detail}</p>
                  )}
                </div>

                <div className="flex gap-4 text-xs">
                  <div className="text-right">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Inscription</p>
                    {familleInfo.politique_ecole ? (
                      <p className="mt-0.5 font-semibold text-[#246990]">Offerte</p>
                    ) : reductionInscription > 0 ? (
                      <p className="mt-0.5 font-semibold text-[#246990]">-{fcfa(reductionInscription)}</p>
                    ) : (
                      <p className="mt-0.5 font-semibold text-gray-400">Aucune</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Mensuel</p>
                    {reductionMensuelle > 0 ? (
                      <p className="mt-0.5 font-semibold text-[#246990]">-{fcfa(reductionMensuelle)}</p>
                    ) : (
                      <p className="mt-0.5 font-semibold text-gray-400">Tarif normal</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}


              {/* Sans comptabilité, le récapitulatif financier n'a rien à
                  récapituler : aucun paiement n'a pu être saisi. On annonce le
                  tarif, ce qui reste l'information utile, plutôt que d'aligner
                  des compteurs à zéro qui laisseraient croire à un impayé. */}
              {!comptabiliteOuverte && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard size={16} className="text-[#246990]" />
                    <h3 className="font-semibold text-gray-900 text-sm">Frais de scolarité</h3>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2 text-sm">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-gray-700">Frais d'inscription</span>
                      <span className="font-semibold text-gray-900 tabular-nums">
                        {montantFCFA(finance?.inscription?.montant)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-gray-700">Total pour l'année</span>
                      <span className="font-bold text-[#246990] tabular-nums">
                        {montantFCFA(totalAnnuelAnnonce)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 pt-1 leading-relaxed">
                      L'échéancier de l'élève est créé et conservé. Son encaissement
                      relève des modules de gestion financière.
                    </p>
                  </div>
                </div>
              )}

              {/* Récapitulatif */}
              {comptabiliteOuverte && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard size={16} className="text-[#246990]" />
                  <h3 className="font-semibold text-gray-900 text-sm">Récapitulatif financier</h3>
                </div>

                {/* Totaux */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xl font-bold text-gray-900">{totalDue.toLocaleString()} <span className="text-xs font-normal">FCFA</span></p>
                    <p className="text-xs text-gray-500">Total dû</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xl font-bold text-green-600">{totalPaid.toLocaleString()} <span className="text-xs font-normal">FCFA</span></p>
                    <p className="text-xs text-gray-500">Payé</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xl font-bold text-orange-600">{remaining.toLocaleString()} <span className="text-xs font-normal">FCFA</span></p>
                    <p className="text-xs text-gray-500">Reste</p>
                  </div>
                </div>

                {/* Frais d'inscription */}
                {finance.inscription?.montant > 0 && (
                  <div className="mb-2 p-3 bg-white border border-gray-200 rounded">
                    <div className="flex justify-between items-start mb-2">
                      <p className="font-medium text-gray-900 text-sm">Frais d'inscription</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        finance.inscription.paye ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {finance.inscription.paye ? 'Payé' : 'Non payé'}
                      </span>
                    </div>
                    {finance.inscription.montant_reduction > 0 ? (
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between text-gray-500">
                          <span>Montant normal</span>
                          <span>{(finance.inscription.montant_normal || 0).toLocaleString()} FCFA</span>
                        </div>
                        <div className="flex justify-between text-green-600 font-medium">
                          <span>Réduction</span>
                          <span>-{(finance.inscription.montant_reduction || 0).toLocaleString()} FCFA</span>
                        </div>
                        <div className="flex justify-between font-bold text-gray-900 text-base pt-1 border-t">
                          <span>À payer</span>
                          <span>{finance.inscription.montant.toLocaleString()} FCFA</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between font-bold text-gray-900 text-base">
                        <span>Montant</span>
                        <span>{finance.inscription.montant.toLocaleString()} FCFA</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Paiements mensuels.
                    Les mois non scolarises comptent dans cette condition : sinon
                    un eleve dont tous les mois anterieurs sont a zero voyait la
                    section disparaitre, sans jamais savoir ce qui avait ete
                    decide de ces mois. */}
                {(paiementsSummary.paye.count + paiementsSummary.partiel.count + paiementsSummary.nonPaye.count
                  + paiementsSummary.nonScolarise.count + paiementsSummary.prorata.count) > 0 && (
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-2">Paiements mensuels</p>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Mois payés */}
                      {paiementsSummary.paye.count > 0 && (
                        <div className="p-3 bg-white border border-gray-200 rounded">
                          <div className="flex items-center gap-1 mb-2">
                            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{paiementsSummary.paye.count}</span>
                            </div>
                            <p className="text-xs font-medium text-gray-900">Payés</p>
                          </div>
                          {paiementsSummary.paye.reduction > 0 ? (
                            <div className="text-xs space-y-1">
                              <div className="flex justify-between text-gray-500">
                                <span>Normal</span>
                                <span>{paiementsSummary.paye.montantNormal.toLocaleString()} FCFA</span>
                              </div>
                              <div className="flex justify-between text-green-600">
                                <span>Réduction</span>
                                <span>-{paiementsSummary.paye.reduction.toLocaleString()} FCFA</span>
                              </div>
                              <div className="flex justify-between font-bold text-green-600 text-sm pt-1 border-t">
                                <span>Payé</span>
                                <span>{paiementsSummary.paye.montant.toLocaleString()} FCFA</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-base font-bold text-green-600">{paiementsSummary.paye.montant.toLocaleString()} FCFA</p>
                          )}
                          <p className="text-xs text-gray-500 mt-2 truncate">{paiementsSummary.paye.mois.join(', ')}</p>
                        </div>
                      )}

                      {/* Mois non payés */}
                      {paiementsSummary.nonPaye.count > 0 && (
                        <div className="p-3 bg-white border border-gray-200 rounded">
                          <div className="flex items-center gap-1 mb-2">
                            <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{paiementsSummary.nonPaye.count}</span>
                            </div>
                            <p className="text-xs font-medium text-gray-900">Non payés</p>
                          </div>
                          {paiementsSummary.nonPaye.reduction > 0 ? (
                            <div className="text-xs space-y-1">
                              <div className="flex justify-between text-gray-500">
                                <span>Normal</span>
                                <span>{paiementsSummary.nonPaye.montantNormal.toLocaleString()} FCFA</span>
                              </div>
                              <div className="flex justify-between text-green-600">
                                <span>Réduction</span>
                                <span>-{paiementsSummary.nonPaye.reduction.toLocaleString()} FCFA</span>
                              </div>
                              <div className="flex justify-between font-bold text-red-600 text-sm pt-1 border-t">
                                <span>À payer</span>
                                <span>{paiementsSummary.nonPaye.montant.toLocaleString()} FCFA</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-base font-bold text-red-600">{paiementsSummary.nonPaye.montant.toLocaleString()} FCFA</p>
                          )}
                          <p className="text-xs text-gray-500 mt-2 truncate">{paiementsSummary.nonPaye.mois.join(', ')}</p>
                        </div>
                      )}

                      {/* Mois anterieurs a l'entree : rien n'est du.
                          Sans cette carte ils gonflaient le total et le
                          recapitulatif reclamait neuf mois a un eleve arrive
                          en juin. */}
                      {paiementsSummary.nonScolarise.count > 0 && (
                        <div className="p-3 bg-white border border-gray-200 rounded">
                          <div className="flex items-center gap-1 mb-2">
                            <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{paiementsSummary.nonScolarise.count}</span>
                            </div>
                            <p className="text-xs font-medium text-gray-900">Non scolarisés</p>
                          </div>
                          <p className="text-base font-bold text-gray-500">0 FCFA</p>
                          <p className="text-xs text-gray-500 mt-2">
                            Antérieurs à son entrée : rien n'est dû.
                          </p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{paiementsSummary.nonScolarise.mois.join(', ')}</p>
                        </div>
                      )}

                      {/* Mois d'entree au prorata : montant reduit, mais
                          integralement du. La distinction compte - un prorata
                          regle est solde, pas partiel. */}
                      {paiementsSummary.prorata.count > 0 && (
                        <div className="p-3 bg-[#f6fbfd] border border-[#cfe3ec] rounded">
                          <div className="flex items-center gap-1 mb-2">
                            <div className="w-4 h-4 bg-[#246990] rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{paiementsSummary.prorata.count}</span>
                            </div>
                            <p className="text-xs font-medium text-gray-900">Au prorata</p>
                          </div>
                          {paiementsSummary.prorata.lignes.map((ligne) => (
                            <div key={ligne.mois} className="text-xs space-y-1">
                              <div className="flex justify-between text-gray-500">
                                <span>{ligne.mois} — tarif plein</span>
                                <span className="line-through">{fcfa(ligne.montantPlein)}</span>
                              </div>
                              <div className="flex justify-between text-[#246990]">
                                <span>{ligne.joursPresents} jour{ligne.joursPresents > 1 ? 's' : ''} sur {ligne.joursDuMois}</span>
                                <span className="font-bold">{fcfa(ligne.montantDu)}</span>
                              </div>
                            </div>
                          ))}
                          <p className="text-xs text-gray-500 mt-2">
                            Arrivé en cours de mois. Ce montant est intégralement dû.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Avertissement */}
              {remaining > 0 && (
                <div className="flex gap-2 p-2 bg-orange-50 border border-orange-200 rounded">
                  <AlertCircle className="text-orange-600 flex-shrink-0" size={16} />
                  <p className="text-xs text-gray-700">
                    Reste à payer : <strong className="text-orange-600">{remaining.toLocaleString()} FCFA</strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* L'impression automatique ouvre le tiroir : le rappeler avant de
            confirmer une inscription payée (décision de Daniel du 04/11/2026). */}
        <div className="px-5 pb-3">
          <AvisEncaissement />
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 rounded-b-lg border-t border-gray-200 flex justify-between items-center">
          <p className="text-xs text-gray-600">
            Inscription de <strong className="text-gray-900">{studentInfo.prenom} {studentInfo.nom}</strong>
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            >
              Annuler
            </button>
            <button
              onClick={isSubmitting ? undefined : onConfirm}
              disabled={isSubmitting}
              className={`inline-flex min-w-[205px] items-center justify-center gap-2 px-5 py-2 text-sm text-white rounded-lg transition-colors font-medium ${
                isSubmitting
                  ? 'bg-[#246990]/70 cursor-not-allowed'
                  : 'bg-[#246990] hover:bg-[#1a4d6e]'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Confirmation en cours...
                </>
              ) : (
                "Confirmer l'inscription"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;